import { useEffect, useState } from "react";
import { isActiveEmp } from "../lib/constants";
import { toast } from "sonner";
import { Clock, Loader2, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { api } from "../lib/api";

const Row = ({ label, value, onChange, onRemove, testid }) => (
  <div className="flex items-center gap-2 text-xs" data-testid={testid}>
    <span className="flex-1 min-w-0 truncate">{label}</span>
    <Input type="time" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-8 w-28" />
    {onRemove && <button onClick={onRemove}><X className="h-3.5 w-3.5 text-slate-400 hover:text-rose-500" /></button>}
  </div>
);

export const AttendanceShiftSettings = ({ employees, branchNames }) => {
  const [s, setS] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.attShiftSettings().then(setS).catch(() => {}); }, []);
  if (!s) return null;
  const byId = Object.fromEntries(employees.map((e) => [e.id, e]));
  const setMap = (k, key, v) => {
    const m = { ...s[k] };
    if (v) m[key] = v; else delete m[key];
    setS({ ...s, [k]: m });
  };
  const save = async () => {
    setBusy(true);
    try { setS(await api.saveAttShiftSettings(s)); toast.success("تم حفظ أوقات الدوام"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="att-shift-settings">
      <p className="font-heading font-bold text-slate-700 text-sm flex items-center gap-1.5"><Clock className="h-4 w-4 text-amber-600" /> بداية الدوام وحساب التأخير</p>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-slate-500">بداية الدوام (عام)</label>
          <Input type="time" value={s.default_start} onChange={(e) => setS({ ...s, default_start: e.target.value })} data-testid="att-shift-default" /></div>
        <div><label className="text-xs text-slate-500">فترة السماح (دقيقة)</label>
          <Input type="number" min={0} max={180} value={s.grace} onChange={(e) => setS({ ...s, grace: Number(e.target.value) })} data-testid="att-shift-grace" /></div>
      </div>
      <p className="text-[11px] text-slate-500">من يبصم بعد البداية + فترة السماح يُسجَّل «متأخر» وتُحسب الدقائق من بداية الدوام.</p>
      {branchNames.length > 0 && (
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-1.5" data-testid="att-shift-branches">
          <p className="text-xs font-semibold text-slate-600">وقت خاص لكل فرع (اتركه فارغاً للوقت العام):</p>
          {branchNames.map((b) => <Row key={b} label={b} value={s.branches[b]} onChange={(v) => setMap("branches", b, v)} testid={`att-shift-branch-${b}`} />)}
        </div>
      )}
      <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-1.5" data-testid="att-shift-employees">
        <p className="text-xs font-semibold text-slate-600">وقت خاص لموظفين محددين:</p>
        {Object.entries(s.employees).map(([id, v]) => (
          <Row key={id} label={byId[id]?.name || id} value={v} onChange={(t) => setMap("employees", id, t || v)} onRemove={() => setMap("employees", id, "")} testid={`att-shift-emp-${id}`} />
        ))}
        <select value={pick} onChange={(e) => { if (e.target.value) setMap("employees", e.target.value, s.default_start); setPick(""); }}
          className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="att-shift-emp-pick">
          <option value="">+ إضافة موظف بوقت خاص…</option>
          {employees.filter((e) => isActiveEmp(e) && !s.employees[e.id]).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>
      <AbsenceBlock s={s} setS={setS} />
      <Button size="sm" onClick={save} disabled={busy} className="gap-1.5" data-testid="att-shift-save">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ أوقات الدوام
      </Button>
    </div>
  );
};

const DAYS = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

const AbsenceBlock = ({ s, setS }) => {
  const [busy, setBusy] = useState(false);
  const toggleDay = (d) => setS({ ...s, weekends: s.weekends.includes(d) ? s.weekends.filter((x) => x !== d) : [...s.weekends, d] });
  const runNow = async () => {
    if (!window.confirm("سيتم تسجيل غياب اليوم لكل من له رقم في جهاز البصمة ولم يبصم. متابعة؟")) return;
    setBusy(true);
    try { const r = await api.runAbsence(); toast.success(`تم تسجيل ${r.absent.length} غائب`); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التشغيل"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl bg-rose-50/40 border border-rose-100 p-3 space-y-2" data-testid="att-absence-settings">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-700">الغياب التلقائي (لمن له رقم في جهاز البصمة)</p>
        <Switch checked={s.absence_enabled} onCheckedChange={(v) => setS({ ...s, absence_enabled: v })} data-testid="att-absence-enabled" />
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-500">نهاية الدوام:</span>
        <Input type="time" value={s.end_time} onChange={(e) => setS({ ...s, end_time: e.target.value })} className="h-8 w-28" data-testid="att-absence-end" />
      </div>
      <div className="flex flex-wrap gap-1" data-testid="att-absence-weekends">
        <span className="text-xs text-slate-500 ml-1">الإجازة الأسبوعية:</span>
        {DAYS.map((n, i) => (
          <button key={i} type="button" onClick={() => toggleDay(i)} data-testid={`att-weekend-${i}`}
            className={`rounded-full px-2 py-0.5 text-[11px] border ${s.weekends.includes(i) ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-600 border-slate-200"}`}>{n}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <label className="flex items-center gap-1"><input type="checkbox" checked={s.notify_whatsapp} onChange={(e) => setS({ ...s, notify_whatsapp: e.target.checked })} data-testid="att-absence-wa" /> ملخص واتساب للمدير</label>
        <label className="flex items-center gap-1"><input type="checkbox" checked={s.notify_telegram} onChange={(e) => setS({ ...s, notify_telegram: e.target.checked })} data-testid="att-absence-tg" /> ملخص تيليجرام</label>
        <Button size="sm" variant="outline" className="h-7 text-xs mr-auto" disabled={busy} onClick={runNow} data-testid="att-absence-run">
          {busy && <Loader2 className="h-3 w-3 animate-spin" />} تسجيل غياب اليوم الآن
        </Button>
      </div>
      <p className="text-[11px] text-slate-500">يُستثنى من عنده سجل إجازة/استئذان، أو حالته إجازة / خروج وعودة / موقوف. احفظ الإعدادات بعد التعديل.</p>
    </div>
  );
};
